import { useEffect, useState } from "react";
import { Outlet, NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useUserPreferences } from "@/lib/userPrefs";
import api from "@/lib/api";
import ForceCheckinModal from "@/components/attendance/ForceCheckinModal";
import IdleLogoutWatcher from "@/components/IdleLogoutWatcher";
import ActivitySessionKeeper from "@/components/ActivitySessionKeeper";
import AgentConsoleWidget from "@/components/AgentConsoleWidget";
import { useAgentsLive } from "@/lib/useAgentsLive";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import PolicyGateModal from "@/components/policies/PolicyGateModal";
import { useTheme } from "@/lib/theme";
import {
  LayoutDashboard, Users, FolderKanban, ListChecks, UserCog, CreditCard, LogOut, Settings as SettingsIcon, Shield, Gift, Activity, Search, Menu, X, Download, HardDrive, Trophy, AlertTriangle, ShieldCheck, ShieldAlert, Calendar, ClipboardCheck, ClipboardList, User as UserIconLucide, UserSquare2, Briefcase, FileText, Megaphone, Building2, BookOpen, ChevronDown, ChevronRight, Lock, Award, Map as MapIconLucide, Package, Target, Clock3, Gem, CalendarDays, Plug, Mail, Bot, Wallet, Truck, PieChart, Server, HandCoins, Rocket, HeartHandshake, Ticket, ScrollText, Sparkles, Network, Radio, Upload, PhoneCall, TrendingUp, Layers, Repeat, Zap, Bell, BellRing, AlarmClock, Lightbulb, Palette, MessageCircle, Code2, Receipt,
} from "lucide-react";
import { Grid3x3, MessageSquare, BarChart3, Radar, History, Ban, Inbox } from "lucide-react";
import { Workflow } from "lucide-react";
import { CalendarClock } from "lucide-react";
import { FileText as DocsIcon } from "lucide-react";
import { Trophy as SalesPerfIcon } from "lucide-react";
import { Facebook } from "lucide-react";
import { LayoutGrid } from "lucide-react";
import { Webhook } from "lucide-react";
import { IndianRupee } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import NotificationsBell from "@/components/NotificationsBell";
import RemindersBell from "@/components/RemindersBell";
import TimesheetWidget from "@/components/TimesheetWidget";
import DailyPlanHeaderButton from "@/components/DailyPlanHeaderButton";
import HeaderCreateButton from "@/components/HeaderCreateButton";
import ReminderPopup from "@/components/ReminderPopup";
import FloatingWaWidget from "@/components/wa/FloatingWaWidget";
import WhatsAppHeaderButton from "@/components/wa/WhatsAppHeaderButton";
import WhatsAppLiveAlerts from "@/components/wa/WhatsAppLiveAlerts";
import WatchListHeaderButton from "@/components/WatchListHeaderButton";
import NotesHeaderButton from "@/components/NotesHeaderButton";
import MyJourneyHeaderButton from "@/components/Layout/MyJourneyHeaderButton";
import PageTelemetry from "@/components/PageTelemetry";
import CommandPalette from "@/components/CommandPalette";
import DailyPlanReminderBanner from "@/components/DailyPlanReminderBanner";
import AppearanceApplier from "@/components/AppearanceApplier";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import useBootstrap from "@/lib/useBootstrap";
import useRecentPages from "@/lib/useRecentPages";
import HeaderProfile from "@/components/HeaderProfile";
import HeaderMobileOverflow from "@/components/HeaderMobileOverflow";
import CelebrationLayer from "@/components/CelebrationLayer";
import HuddleJoinWidget from "@/components/HuddleJoinWidget";
import ScreenshotReporter from "@/components/ScreenshotReporter";
import GlobalFeedbackLauncher from "@/components/GlobalFeedbackLauncher";
import JobsIndicator from "@/components/JobsIndicator";
import CelebrationOverlay from "@/components/CelebrationOverlay";
import FestivalBanner from "@/components/FestivalBanner";
import { SopRunnerProvider } from "@/components/sops/SopRunnerContext";
import { EmployeeProfileProvider } from "@/lib/employeeProfile";
import { useComplianceBadge } from "@/lib/useComplianceBadge";
import ErrorBoundary from "@/components/ErrorBoundary";

// Iter Feb 2026 R34 — Guard branding image src against non-string payloads.
// Prevents a malformed logo upload (object leaked through /branding) from
// throwing during render and white-screening the app.
const _brandSrc = (v) => (typeof v === "string" && v.trim() ? v : "");

// R159oo · Feb 2026 — Mobile menu button icon. Always renders the
// hamburger `Menu` glyph as the default. Only overlays the workspace
// square logo when `logo_icon_url` is explicitly set AND the loaded
// image has meaningful dimensions (guards against stub 1x1 PNGs some
// tenants leave in the branding row). If the icon URL 404s or the
// image is too small to be a real icon, the hamburger stays visible.
function MobileHeaderBrandIcon({ branding }) {
  const url = _brandSrc(branding?.logo_icon_url);
  const [ok, setOk] = useState(false);
  const [broken, setBroken] = useState(false);
  const showLogo = !!url && ok && !broken;
  return (
    <span className="relative w-6 h-6 flex items-center justify-center">
      {!showLogo && <Menu className="w-5 h-5" data-testid="mobile-menu-hamburger" />}
      {url && !broken && (
        <img
          src={url}
          alt={branding?.platform_name || ""}
          className={`absolute inset-0 w-6 h-6 object-contain ${showLogo ? "opacity-100" : "opacity-0"}`}
          data-testid="mobile-menu-brand-icon"
          onLoad={(e) => {
            // Reject stub 1x1 (or smaller than 8px) images — they render
            // as an invisible dot and mask the hamburger. Keep the
            // hamburger visible in that case.
            const w = e.currentTarget.naturalWidth || 0;
            const h = e.currentTarget.naturalHeight || 0;
            if (w >= 8 && h >= 8) setOk(true); else setBroken(true);
          }}
          onError={() => setBroken(true)}
        />
      )}
    </span>
  );
}


export const HEADER_ITEMS = [
  { key: "header-my-score", label: "My Score widget" },
  { key: "header-search", label: "Global search" },
  { key: "header-notifications", label: "Notifications bell" },
  { key: "header-reminders", label: "Reminders panel" },
  // R138e · Feb 2026 — Granular gating for every header widget so an
  // admin can strip the bar down to the essentials for a role
  // (e.g. Client Viewers get no Create / no Timesheet / no WhatsApp).
  { key: "header-profile", label: "Profile / Impersonation chip" },
  { key: "header-create", label: "+ Create menu" },
  { key: "header-daily-plan", label: "Daily plan button" },
  { key: "header-timesheet", label: "Timesheet button" },
  { key: "header-jobs", label: "Jobs (async task) indicator" },
  { key: "header-whatsapp", label: "WhatsApp header button" },
  { key: "header-notes", label: "Accounts notes indicator" },
  { key: "header-clock-in", label: "Clock-in / Compliance widgets" },
  { key: "header-timesheet-warning", label: "Red daily-plan warning banner" },
];

// Feb 2026 · Sidebar reorganized to mirror the RBAC "Module → Screen"
// taxonomy (see backend/routes_roles.py `PERMISSION_MODULES`). Every
// item literal is preserved — only the parent groups change. If you
// add a new page here, add its `perm` list to the matching backend
// permission catalog entry too so the RBAC editor exposes it.
export const NAV_GROUPS = [
  {
    key: "personal", label: "Me",
    items: [
      { to: "/app/me", label: "My dashboard", icon: UserSquare2, testid: "nav-my-dashboard" },
      { to: "/app/my/journey", label: "My Journey", icon: Sparkles, testid: "nav-my-journey" },
      { to: "/app/my/approvals", label: "My Approvals", icon: Inbox, testid: "nav-my-approvals" },
      { to: "/app/reminders/team", label: "Team Reminders", icon: AlarmClock, testid: "nav-team-reminders" },
      { to: "/app/replanning", label: "Replanning", icon: CalendarClock, testid: "nav-replanning" },
      { to: "/app/assignments", label: "My assignments", icon: ClipboardCheck, testid: "nav-assignments" },
      { to: "/app/buddy", label: "Buddy Program", icon: HeartHandshake, testid: "nav-buddy" },
      { to: "/app/helpdesk", label: "Helpdesk", icon: Ticket, testid: "nav-helpdesk" },
      { to: "/app/huddles", label: "Huddles", icon: Radio, testid: "nav-huddles" },
      { to: "/app/lms/my-learning", label: "My Learning", icon: BookOpen, testid: "nav-my-learning" },
      { to: "/app/lms/my-quizzes", label: "My Quizzes", icon: BookOpen, testid: "nav-my-quizzes" },
      { to: "/app/my-attendance", label: "My attendance & leaves", icon: Calendar, testid: "nav-my-attendance" },
      { to: "/app/idp/me", label: "My IDP", icon: Target, testid: "nav-idp-me" },
    ],
  },
  {
    key: "workspace", label: "Workspace",
    items: [
      { to: "/app", label: "Bulletin board", icon: Megaphone, end: true, testid: "nav-bulletin" },
      { to: "/app/policies", label: "Policies", icon: ScrollText, testid: "nav-policies", perm: ["policy.view"] },
      { to: "/app/company-qa", label: "Company Q&A", icon: BookOpen, testid: "nav-company-qa", perm: ["qa.ask"] },
      { to: "/app/engagement", label: "Engagement widget", icon: Sparkles, testid: "nav-engagement", perm: ["engagement.manage"] },
      { to: "/app/calendar", label: "Calendar", icon: Calendar, testid: "nav-calendar" },
      { to: "/app/map", label: "Map", icon: MapIconLucide, testid: "nav-map", perm: ["workspace.map.view", "workspace.map.manage", "project.view", "lead.view", "client.view"] },
      { to: "/app/activity", label: "Activity", icon: Activity, testid: "nav-activity" },
      { to: "/app/notifications", label: "Notifications", icon: Megaphone, testid: "nav-notifications" },
    ],
  },
  {
    key: "projects_tasks", label: "Projects & Tasks",
    items: [
      { to: "/app/projects", label: "Projects", icon: FolderKanban, testid: "nav-projects", perm: ["project.view"] },
      { to: "/app/planning", label: "Planning board", icon: LayoutGrid, testid: "nav-planning", perm: ["project.view"] },
      { to: "/app/portfolios", label: "Executive portfolios", icon: TrendingUp, testid: "nav-portfolios", perm: ["portfolio.view", "portfolio.manage", "workspace.manage"] },
      { to: "/app/goals", label: "Goals & OKRs", icon: Target, testid: "nav-goals" },
      { to: "/app/tasks", label: "Tasks", icon: ListChecks, testid: "nav-tasks", perm: ["task.view"] },
      { to: "/app/task-intelligence", label: "Task intelligence", icon: BarChart3, testid: "nav-task-intelligence", perm: ["task.intelligence.view"] },
      { to: "/app/reports/daily-completion", label: "Daily completion report", icon: BarChart3, testid: "nav-daily-completion", perm: ["task.view"] },
      { to: "/app/reports/compliance-monitor", label: "Compliance monitor", icon: ShieldAlert, testid: "nav-compliance-monitor", perm: ["compliance.view", "compliance.configure"] },
      { to: "/app/reports/timesheet-advanced", label: "Timesheet report", icon: BarChart3, testid: "nav-timesheet-advanced", perm: ["timesheet.report_view", "timesheet.report_all"] },
      { to: "/app/action-items", label: "Action items", icon: ClipboardCheck, testid: "nav-action-items", perm: ["task.view"] },
      { to: "/app/meetings", label: "Meetings & Comms", icon: Calendar, testid: "nav-meetings", perm: ["meeting.view.internal", "meeting.view.external", "meeting.manage"] },
      { to: "/app/feedback", label: "Client feedback", icon: MessageSquare, testid: "nav-feedback" },
      { to: "/app/log", label: "Log", icon: AlertTriangle, testid: "nav-log", perm: ["raid.manage", "project.view"] },
      { to: "/app/quality-review", label: "Quality", icon: AlertTriangle, testid: "nav-quality", perm: ["quality.view", "quality.manage", "project.view"] },
      { to: "/app/project-intel", label: "Project Intelligence", icon: Radar, testid: "nav-project-intel" },
      { to: "/app/diagrams", label: "Diagrams", icon: Workflow, testid: "nav-diagrams" },
      { to: "/app/requirement-briefs", label: "Requirements gathering", icon: Sparkles, testid: "nav-requirement-briefs", perm: ["quotation.view", "quotation.manage", "questionnaire.view", "questionnaire.manage"] },
    ],
  },
  {
    key: "hr_people", label: "HR & People",
    items: [
      { to: "/app/users", label: "User management", icon: Users, testid: "nav-users", perm: ["workspace.member.manage", "workspace.member.invite", "hr.manage"] },
      { to: "/app/hr-dashboard", label: "HR Dashboard", icon: Activity, testid: "nav-hr-dashboard", perm: ["hr.approve", "hr.manage", "timesheet.report"] },
      { to: "/app/employees", label: "Employees", icon: UserSquare2, testid: "nav-employees", perm: ["employee.view"] },
      { to: "/app/employees/dashboard", label: "Employees · Intelligence", icon: Activity, testid: "nav-employees-dashboard", perm: ["employee.view", "employee.manage"] },
      { to: "/app/people", label: "People Analytics", icon: Users, testid: "nav-people", perm: ["hr.approve", "hr.manage", "timesheet.report"] },
      { to: "/app/team", label: "Team (legacy)", icon: UserCog, testid: "nav-team", perm: ["workspace.member.manage"] },
      { to: "/app/timesheet", label: "Timesheet", icon: Clock3, testid: "nav-timesheet" },
      { to: "/app/attendance", label: "Attendance & Leave (HR)", icon: Calendar, testid: "nav-attendance", perm: ["hr.manage", "hr.approve"] },
      { to: "/app/now", label: "Now — Command Center", icon: Radio, testid: "nav-now" },
      { to: "/app/hr/onboarding", label: "Onboarding & Alumni", icon: Rocket, testid: "nav-hr-onboarding", perm: ["employee.manage", "hr.manage"] },
      { to: "/app/hr/templates", label: "Onboarding Templates", icon: LayoutGrid, testid: "nav-hr-templates", perm: ["employee.manage", "hr.manage"] },
      { to: "/app/hr/pip", label: "PIP", icon: ShieldAlert, testid: "nav-pip", perm: ["employee.manage", "hr.manage"] },
      { to: "/app/hr/designations", label: "Designations", icon: Award, testid: "nav-designations", perm: ["designation.view", "designation.manage"] },
      { to: "/app/hr/job-duties", label: "Job Duties & Reviews", icon: ClipboardList, testid: "nav-job-duties", perm: ["hr.job_duty.view_own", "hr.job_duty.review", "hr.job_duty.manage_all"] },
      { to: "/app/hr/profile-edits", label: "Profile edit approvals", icon: ShieldCheck, testid: "nav-profile-edits", perm: ["employee.manage", "workspace.manage"] },
      { to: "/app/hr/comp-off-rules", label: "Comp-Off rules", icon: CalendarClock, testid: "nav-comp-off-rules", perm: ["hr.manage"] },
      { to: "/app/hr/corrections", label: "HR corrections", icon: ShieldAlert, testid: "nav-hr-corrections", perm: ["hr.manage"] },
      { to: "/app/hr/compensation", label: "Compensation", icon: IndianRupee, testid: "nav-hr-compensation", perm: ["hr.compensation.manage", "workspace.admin"] },
      { to: "/app/org-chart", label: "Org Chart", icon: Network, testid: "nav-org-chart", perm: ["employee.view"] },
      { to: "/app/departments", label: "Departments", icon: Building2, testid: "nav-departments", perm: ["employee.view", "department.manage"] },
      { to: "/app/idp", label: "Development Plans (IDP)", icon: Target, testid: "nav-idp-admin", perm: ["idp.manage", "hr.manage", "workspace.admin"] },
      { to: "/app/skill-center", label: "Skill center", icon: Award, testid: "nav-skill-center" },
      { to: "/app/access/team-matrix", label: "Team Access Matrix", icon: Grid3x3, testid: "nav-team-matrix", perm: ["workspace.role.manage", "workspace.manage"] },
      { to: "/app/access/allocation", label: "Team Allocation", icon: LayoutGrid, testid: "nav-team-allocation", perm: ["workspace.role.manage", "workspace.manage"] },
    ],
  },
  {
    key: "crm_sales", label: "CRM & Sales",
    items: [
      { to: "/app/leads", label: "Leads", icon: Briefcase, testid: "nav-leads", perm: ["lead.view"] },
      { to: "/app/enquiries", label: "Enquiries", icon: Inbox, testid: "nav-enquiries", perm: ["enquiry.view", "enquiry.manage"] },
      { to: "/app/forms", label: "Forms", icon: FileText, testid: "nav-forms", perm: ["lead.view", "lead.manage"] },
      { to: "/app/studio", label: "Studio", icon: Radar, testid: "nav-studio" },
      { to: "/app/accounts", label: "Accounts", icon: Building2, testid: "nav-accounts", perm: ["lead.view"] },
      { to: "/app/prospecting", label: "Prospecting", icon: Search, testid: "nav-prospecting", perm: ["prospect.view"] },
      { to: "/app/clients", label: "Clients", icon: Users, testid: "nav-clients", perm: ["client.view"] },
      { to: "/app/onboarding-kits", label: "Onboarding kits", icon: Rocket, testid: "nav-onboarding-kits", perm: ["client.view"] },
      { to: "/app/brands", label: "Brands", icon: Gem, testid: "nav-brands", module: "brands", perm: ["brand.view", "brand.manage", "client.view"] },
      { to: "/app/packages", label: "Packages", icon: Package, testid: "nav-packages", perm: ["package.view", "package.manage", "brand.view", "client.view"] },
      { to: "/app/calls", label: "Calls (MCUBE)", icon: PhoneCall, testid: "nav-calls" },
      // Feb 2026 · AI Voice Agents + outbound campaigns. Gated on
      // `voice_agent.view` so a workspace owner can keep ops staff out
      // of the agent trainer while still letting SDRs run campaigns.
      { to: "/app/voice-agents", label: "Voice Agents", icon: Bot, testid: "nav-voice-agents", perm: ["voice_agent.view", "voice_agent.manage", "marketing.view"] },
      { to: "/app/voice-campaigns", label: "Voice Campaigns", icon: Radio, testid: "nav-voice-campaigns", perm: ["voice_agent.view", "voice_agent.manage", "marketing.view", "crm.view"] },
      { to: "/app/sales-cc", label: "Sales Command", icon: Zap, testid: "nav-sales-cc", perm: ["quotation.view", "sales_order.view", "invoice.view", "payment.view"] },
      { to: "/app/sales-perf", label: "Sales Performance", icon: SalesPerfIcon, testid: "nav-sales-perf", perm: ["sales_perf.view", "sales_perf.manage"] },
      { to: "/app/rfqs", label: "RFQs", icon: FileText, testid: "nav-rfqs", perm: ["quotation.view", "quotation.manage", "estimate.view", "estimate.manage", "workspace.manage"] },
      { to: "/app/estimates", label: "Estimates", icon: FileText, testid: "nav-estimates", perm: ["quotation.view", "quotation.manage", "estimate.view", "estimate.manage", "workspace.manage"] },
      { to: "/app/items", label: "Item catalog", icon: Package, testid: "nav-items", perm: ["quotation.view", "quotation.manage", "estimate.view", "estimate.manage", "workspace.manage"] },
      { to: "/app/contracts", label: "Contracts", icon: FileText, testid: "nav-contracts", perm: ["quotation.view", "quotation.manage", "estimate.view", "estimate.manage", "workspace.manage"] },
      { to: "/app/contract-templates", label: "Templates", icon: FileText, testid: "nav-tpl", perm: ["quotation.manage", "workspace.manage"] },
      { to: "/app/delivery", label: "Delivery Board", icon: FileText, testid: "nav-delivery", perm: ["delivery.view", "delivery.manage", "project.view", "task.view", "sales_order.view", "workspace.manage"] },
      { to: "/app/profitability", label: "Profitability", icon: FileText, testid: "nav-profitability", perm: ["estimate.view_rates", "quotation.view", "invoice.view", "workspace.manage"] },
      { to: "/app/line-analytics", label: "Line Analytics", icon: FileText, testid: "nav-line-analytics", perm: ["quotation.view", "estimate.view", "sales_order.view", "workspace.manage"] },
      { to: "/app/estimates/variance", label: "Variance Dashboard", icon: TrendingUp, testid: "nav-variance", perm: ["estimate.view_rates", "quotation.view", "invoice.view", "workspace.manage"] },
      { to: "/app/bizdev", label: "Business Development", icon: Lightbulb, testid: "nav-bizdev", perm: ["workspace.manage"] },
      { to: "/app/content-calendar", label: "Content Calendar", icon: CalendarDays, testid: "nav-content-calendar", perm: ["package.view", "package.manage", "brand.view", "client.view"] },
      { to: "/app/content-sheet", label: "Content Sheet", icon: FileText, testid: "nav-content-sheet", perm: ["content_sheet.view", "workspace.admin", "workspace.manage", "content_calendar.view_all"] },
      { to: "/app/delivery-intelligence", label: "Delivery Intel", icon: Activity, testid: "nav-delivery-intel", perm: ["package.view", "package.manage", "brand.view", "client.view"] },
    ],
  },
  {
    key: "finance", label: "Finance",
    items: [
      { to: "/app/finance",              label: "Finance hub",       icon: Wallet,      testid: "nav-finance",              perm: ["finance.view", "finance.manage", "budget.view", "report.view"] },
      { divider: true, label: "Sales · AR" },
      { to: "/app/sales",                label: "Sales",             icon: Receipt,     testid: "nav-sales",                perm: ["quotation.view", "sales_order.view", "invoice.view", "payment.view", "finance.view", "finance.manage"] },
      { to: "/app/finance/receivables",  label: "Receivables (AR)",  icon: HandCoins,   testid: "nav-finance-receivables",  perm: ["finance.view", "finance.manage", "invoice.view", "invoice.manage"] },
      { to: "/app/finance/dunning",      label: "Dunning queue",     icon: Bell,        testid: "nav-finance-dunning",      perm: ["finance.view", "invoice.view", "finance.manage", "invoice.manage"] },
      { divider: true, label: "Purchases · AP" },
      { to: "/app/finance/vendors",      label: "Purchases",         icon: Truck,       testid: "nav-finance-vendors",      perm: ["finance.view", "finance.manage", "expense.view", "expense.manage"] },
      { to: "/app/finance/project-spend", label: "Project Spend",    icon: FolderKanban, testid: "nav-finance-project-spend", perm: ["finance.view", "finance.manage", "expense.view", "expense.manage"] },
      { to: "/app/finance/pay-run",      label: "Payroll & Pay-run", icon: Wallet,      testid: "nav-finance-pay-run",      perm: ["finance.view", "finance.manage", "expense.view", "expense.manage"] },
      { to: "/app/finance/bookkeeping",  label: "Bookkeeping",       icon: BookOpen,    testid: "nav-finance-bookkeeping",  perm: ["finance.view", "finance.manage", "budget.view", "report.view"] },
      { divider: true, label: "Reports · Insight" },
      { to: "/app/finance/revenue-flow", label: "Revenue flow",      icon: TrendingUp,  testid: "nav-revenue-flow",         perm: ["finance.view", "finance.manage", "budget.view", "report.view", "invoice.view", "invoice.manage", "lead.view", "client.view"] },
      { to: "/app/finance/reports",      label: "Financial reports", icon: PieChart,    testid: "nav-finance-reports",      perm: ["report.view", "finance.view", "finance.manage", "budget.view"] },
      { to: "/app/finance/leadership",   label: "Leadership view",   icon: FileText,    testid: "nav-finance-leadership",   perm: ["finance.view", "finance.manage", "report.view"] },
      { to: "/app/finance/copilot",      label: "AI Copilot",        icon: Bot,         testid: "nav-finance-copilot",      perm: ["finance.view", "finance.manage"] },
      { to: "/app/finance/scenario-planner", label: "Scenario planner", icon: Sparkles, testid: "nav-scenario-planner",     perm: ["finance.view", "finance.manage", "budget.view", "budget.manage"] },
      { divider: true, label: "Compliance" },
      { to: "/app/finance/msme",         label: "MSME · GST",        icon: Repeat,      testid: "nav-finance-msme",         perm: ["finance.view", "finance.manage", "invoice.view", "invoice.manage"] },
      { to: "/app/finance/close",        label: "Period close",      icon: Lock,        testid: "nav-finance-close",        perm: ["finance.view", "finance.manage"] },
      { to: "/app/finance/advanced",     label: "Advanced (audit)",  icon: Server,      testid: "nav-finance-advanced",     perm: ["finance.view", "finance.manage", "asset.view_finance", "asset.manage_finance"] },
      { to: "/app/settings/billing-profile", label: "Org billing profile", icon: Building2, testid: "nav-billing-profile", perm: ["workspace.admin", "finance.manage", "invoke.manage"] },
    ],
  },
  {
    key: "marketing", label: "Marketing",
    items: [
      { to: "/app/marketing", label: "Marketing", icon: Megaphone, testid: "nav-marketing", perm: ["marketing.view"] },
      { to: "/app/campaigns", label: "Campaign Analytics", icon: Rocket, testid: "nav-campaigns", perm: ["lead.view", "marketing.view"] },
      { to: "/app/suppressions", label: "Suppression List", icon: Ban, testid: "nav-suppressions", perm: ["lead.view", "marketing.view"] },
      { to: "/app/influencers", label: "Influencer marketing", icon: Users, testid: "nav-influencers", perm: ["marketing.view", "brand.view"] },
      { to: "/app/performance", label: "Performance Marketing", icon: BarChart3, testid: "nav-performance", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/performance/lead-ads", label: "Meta Lead Ads", icon: Facebook, testid: "nav-meta-lead-ads", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/performance/multi-brand", label: "Multi-brand", icon: LayoutGrid, testid: "nav-multi-brand", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/performance/attribution", label: "Attribution & ROAS", icon: TrendingUp, testid: "nav-attribution", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/seo", label: "SEO", icon: TrendingUp, testid: "nav-seo", perm: ["brand.view"] },
      { to: "/app/growth-cockpit", label: "Growth Cockpit", icon: Rocket, testid: "nav-growth-cockpit", perm: ["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view", "prospect.view"] },
      { to: "/app/trends", label: "Trends & ideas", icon: TrendingUp, testid: "nav-trends", perm: ["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view"] },
      { to: "/app/marketing/playbooks", label: "Campaign playbooks", icon: Layers, testid: "nav-playbooks", perm: ["marketing.view", "marketing.manage", "brand.view", "brand.manage", "client.view"] },
      { to: "/app/marketing/marketable-days", label: "Marketable Days", icon: CalendarDays, testid: "nav-marketable-days", perm: ["marketing.view", "brand.view", "client.view"] },
    ],
  },
  {
    key: "ops_assets", label: "Ops & Assets",
    items: [
      { to: "/app/assets", label: "Assets", icon: Package, testid: "nav-assets", perm: ["asset.view"] },
      { to: "/app/storage", label: "Storage", icon: HardDrive, testid: "nav-storage", perm: ["workspace.manage"] },
      { to: "/app/import", label: "Import data", icon: Upload, testid: "nav-import", perm: ["workspace.member.manage"] },
    ],
  },
  {
    key: "comms_collab", label: "Comms & Collaboration",
    items: [
      { to: "/app/comms", label: "Comms Center", icon: Bell, testid: "nav-comms-center", perm: ["workspace.manage"] },
      { to: "/app/comms/branding", label: "Email branding", icon: Palette, testid: "nav-comms-branding", perm: ["workspace.manage"] },
      { to: "/app/inbox", label: "Inbox (email)", icon: Mail, testid: "nav-inbox" },
      { to: "/app/webmail", label: "Webmail", icon: Mail, testid: "nav-webmail" },
      { to: "/app/wa-inbox", label: "WhatsApp Inbox", icon: MessageCircle, testid: "nav-wa-inbox", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/wa-templates", label: "WA Templates", icon: BookOpen, testid: "nav-wa-templates", perm: ["marketing.view", "marketing.manage"] },
      { to: "/app/escalations", label: "Escalations", icon: AlertTriangle, testid: "nav-escalations", perm: ["email.view_all"] },
      { to: "/app/auto-response", label: "Auto-Response", icon: Bot, testid: "nav-auto-response", perm: ["email.view_all"] },
      { to: "/app/flows", label: "Flows", icon: Zap, testid: "nav-flows" },
    ],
  },
  {
    key: "support_service", label: "Support & Service",
    items: [
      { to: "/app/helpdesk", label: "Helpdesk (Admin)", icon: Ticket, testid: "nav-helpdesk-admin", perm: ["workspace.manage"] },
    ],
  },
  {
    key: "compliance_security", label: "Compliance & Security",
    items: [
      { to: "/app/compliance", label: "Compliance", icon: ShieldCheck, testid: "nav-compliance" },
      { to: "/app/vault", label: "Credentials vault", icon: Lock, testid: "nav-vault", perm: ["vault.view", "vault.manage"] },
    ],
  },
  {
    key: "docs_portal", label: "Client Portal & Documents",
    items: [
      { to: "/app/sops", label: "SOPs", icon: BookOpen, testid: "nav-sops", perm: ["sop.view"] },
      { to: "/app/documents", label: "Documents", icon: DocsIcon, testid: "nav-documents", perm: ["document.view", "document.manage"] },
    ],
  },
  {
    key: "gamification", label: "Gamification",
    items: [
      { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard", perm: ["leaderboard.view"] },
      { to: "/app/points-history", label: "Points History", icon: History, testid: "nav-points-history" },
    ],
  },
  {
    key: "learning_hiring", label: "Learning & Hiring",
    items: [
      { to: "/app/lms/courses", label: "Learning · Admin", icon: BookOpen, testid: "nav-lms-admin", perm: ["lms.manage", "workspace.admin", "hr.manage"] },
      { to: "/app/lms/standalone-quizzes", label: "Standalone Quizzes", icon: BookOpen, testid: "nav-lms-quizzes", perm: ["lms.manage", "workspace.admin", "hr.manage"] },
      { to: "/app/lms/settings", label: "LMS · Settings", icon: BookOpen, testid: "nav-lms-settings", perm: ["lms.manage", "workspace.admin", "hr.manage"] },
      { to: "/app/lms/hiring", label: "Hiring Assessment Hub", icon: Briefcase, testid: "nav-hiring-hub", perm: ["lms.manage", "workspace.admin", "hr.manage", "hiring.manage"] },
    ],
  },
  {
    key: "events", label: "Events",
    items: [
      { to: "/app/exhibitions", label: "Exhibitions", icon: Building2, testid: "nav-exhibitions", perm: ["sales.view", "sales.manage", "marketing.view", "marketing.manage", "workspace.admin", "exhibitions.view", "exhibitions.manage"] },
      { to: "/app/expo-organizer", label: "Expo Organizer", icon: Building2, testid: "nav-expo-organizer", perm: ["sales.view", "sales.manage", "marketing.view", "marketing.manage", "workspace.admin", "organizer.view", "organizer.manage"] },
    ],
  },
  {
    key: "admin", label: "Workspace & Admin",
    items: [
      { to: "/app/roles", label: "Roles", icon: ShieldCheck, testid: "nav-roles", perm: ["workspace.role.manage"] },
      { to: "/app/developer", label: "Developer", icon: Code2, testid: "nav-developer", perm: ["developer.access"] },
      { to: "/app/webhooks", label: "Webhooks", icon: Webhook, testid: "nav-webhooks", perm: ["workspace.admin", "workspace.manage"] },
      { to: "/app/dashboard-classic", label: "Dashboard (legacy)", icon: LayoutDashboard, testid: "nav-dashboard-classic" },
      { to: "/app/billing", label: "Billing (plan)", icon: CreditCard, testid: "nav-billing", perm: ["workspace.billing"] },
      { to: "/app/referrals", label: "Refer & earn", icon: Gift, testid: "nav-referrals" },
      { to: "/app/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings", perm: ["workspace.manage", "workspace.settings.view"] },
      { to: "/app/integrations", label: "Integrations", icon: Plug, testid: "nav-integrations" },
      { to: "/app/settings/scheduler", label: "Scheduler ops", icon: Server, testid: "nav-scheduler", superAdminOnly: true },
    ],
  },
];

export default function AppShell() {
  const { user, logout, activeWorkspace, memberships, switchWorkspace } = useAuth();
  const { branding } = useTheme();
  // Feb 2026 · R293 — Fire the one-call bootstrap on mount so the shell's
  // six per-page fetches (permissions, compliance badge, hr-settings,
  // active huddles, pending policies, notifications) collapse into one
  // network round-trip. Consumers like `useWorkspacePerms` are seeded via
  // `primeFromBootstrap` so their standalone endpoint is skipped on the
  // first render; refreshes (focus / role change / 60-sec poll) still go
  // through the per-slice endpoints to keep their contracts intact.
  useBootstrap(activeWorkspace?.id);
  const { navOverrides, defaultRoute, ready: permsReady, hasPerm } = useWorkspacePerms();
  const nav = useNavigate();
  const location = useLocation();
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("co_nav_collapsed") === "1"; } catch { return false; }
  });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [navQuery, setNavQuery] = useState("");
  const [allExpandedTick, setAllExpandedTick] = useState(0);  // bump to force NavGroup expand/collapse
  const [allExpandedTarget, setAllExpandedTarget] = useState(null); // true=expand all, false=collapse all, null=idle

  // Track recently viewed pages for the "Recently viewed" widget on the bulletin
  useRecentPages();

  // Role default landing page: redirect once per session when landing on /app
  useEffect(() => {
    if (!permsReady || !defaultRoute || !activeWorkspace?.id) return;
    const key = `acq_landed_${activeWorkspace.id}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch { return; }
    if ((location.pathname === "/app" || location.pathname === "/app/") && defaultRoute !== "/app") {
      nav(defaultRoute, { replace: true });
    }
  }, [permsReady, defaultRoute, activeWorkspace?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    try { localStorage.setItem("co_nav_collapsed", collapsed ? "1" : "0"); } catch { /* ignore */ }
  }, [collapsed]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
      } else if (e.key === "Escape") {
        setSearchOpen(false);
        setDrawerOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);

    // Capture install prompt for PWA
    const onBip = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeinstallprompt", onBip);
    };
  }, []);

  const triggerInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    await installPrompt.userChoice;
    setInstallPrompt(null);
  };

  return (
    <SopRunnerProvider>
    <EmployeeProfileProvider>
    <div className="workspace-shell" data-nav-collapsed={collapsed ? "1" : "0"}>
      <AppearanceApplier />
      <ImpersonationBanner />
      <ForceCheckinModal />
      <IdleLogoutWatcher />
      <ActivitySessionKeeper />
      <AgentConsoleWidget />
      <PolicyGateModal />
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setDrawerOpen(false)}
          data-testid="sidebar-backdrop"
        />
      )}

      <aside
        className={`workspace-sidebar border-r border-border bg-background flex-col z-50 ${drawerOpen ? "flex" : "hidden"} md:flex ${collapsed ? "co-nav-collapsed" : ""}`}
        style={collapsed ? { width: 64 } : undefined}
        data-testid="app-sidebar"
      >
        <div
          className={`px-3 pt-4 pb-2 ${collapsed ? "flex-col gap-2 items-center" : ""}`}
          data-testid="sidebar-header"
        >
          <div className={collapsed ? "flex flex-col gap-2 items-center" : "flex items-center justify-between"}>
          <Link to="/app" onClick={() => setDrawerOpen(false)} className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 relative" data-testid="sidebar-workspace-icon-wrap">
              {_brandSrc(branding.logo_icon_url) ? (
                <img src={_brandSrc(branding.logo_icon_url)} alt={branding.platform_name || ""}
                     className="w-8 h-8 object-contain"
                     data-testid="sidebar-workspace-icon" />
              ) : _brandSrc(branding.logo_url) ? (
                <img src={_brandSrc(branding.logo_url)} alt={branding.platform_name || ""}
                     className="w-8 h-8 object-contain"
                     data-testid="sidebar-workspace-icon" />
              ) : (
                <span className="w-8 h-8 rounded-sm bg-primary text-primary-foreground flex items-center justify-center font-black overflow-hidden">
                  {(branding.platform_name || "A").slice(0, 1)}
                </span>
              )}
            </span>
            {!collapsed && (
              <span className="font-bold tracking-tight truncate flex items-baseline gap-0.5" style={{ fontFamily: "var(--font-heading)" }}>
                {branding.platform_name}
                <sup className="text-[0.5em] text-muted-foreground font-normal" aria-hidden="true">™</sup>
              </span>
            )}
          </Link>
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="hidden md:inline-flex text-foreground/60 hover:text-foreground p-1 border border-transparent hover:border-border"
            data-testid="sidebar-collapse-btn"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand" : "Collapse"}
          >
            <Menu className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => setDrawerOpen(false)}
            className="md:hidden text-foreground/60 hover:text-foreground"
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
          </div>
          {/* Sep 2026 · r-agent-count-chip — small "N agents · live" line
              under the logo. Fake-live count that drifts a bit so it
              feels active without being noisy. */}
          <SidebarAgentsCount collapsed={collapsed} />
          {/* Sep 2026 · r-shimmer-strip — thicker glow line right under
              the agents chip. Occasional light sweep whispers "system
              is alive." */}
          <div className="agent-shimmer-strip" aria-hidden="true" data-testid="sidebar-shimmer-strip" />
        </div>

        {!collapsed && (
        <div className="px-3 py-3 border-b border-border">
          {/* R138g · Feb 2026 — Workspace switcher requires `workspace.switch`
              in the CURRENT workspace. When absent, we render a plain
              (non-clickable) tile so the user still sees which workspace
              they're in but can't move between them. Super-admins skip
              the gate — their access is unconditional. */}
          {(hasPerm("workspace.switch") || hasPerm("platform.settings.access")) ? (
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full text-left px-3 py-2 border border-border hover:border-primary/40 transition" data-testid="workspace-switcher-btn">
              <div className="flex items-center gap-2 min-w-0">
                {activeWorkspace?.logo_url ? (
                  <img src={_brandSrc(activeWorkspace.logo_url)}
                       alt={activeWorkspace?.name || ""}
                       className="w-8 h-8 border border-border object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 border border-border bg-muted text-foreground/70 flex items-center justify-center font-bold text-sm shrink-0">
                    {(activeWorkspace?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="label-eyebrow text-[10px]">Workspace</div>
                  <div className="font-semibold text-sm truncate" data-testid="workspace-switcher-name">{activeWorkspace?.name || "—"}</div>
                </div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56">
              <DropdownMenuLabel>Switch workspace</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {memberships?.map((m) => (
                <DropdownMenuItem key={m.workspace_id} onClick={() => { switchWorkspace(m.workspace_id); nav("/app"); }} data-testid={`ws-switch-${m.workspace_id}`}>
                  {m.workspace?.logo_url ? (
                    <img src={_brandSrc(m.workspace.logo_url)} alt="" className="w-4 h-4 mr-2 border border-border object-cover shrink-0" />
                  ) : (
                    <span className="w-4 h-4 mr-2 border border-border bg-muted text-[10px] font-bold text-foreground/70 flex items-center justify-center shrink-0">
                      {(m.workspace?.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="truncate">{m.workspace?.name || m.workspace_id}</span>
                </DropdownMenuItem>
              ))}
              {(hasPerm("workspace.create") || hasPerm("platform.workspace.create") || hasPerm("platform.settings.access")) && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => nav("/onboarding")} data-testid="ws-create-new">
                    + New workspace
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          ) : (
            <div className="w-full text-left px-3 py-2 border border-border cursor-default"
                 data-testid="workspace-switcher-locked">
              <div className="flex items-center gap-2 min-w-0">
                {activeWorkspace?.logo_url ? (
                  <img src={_brandSrc(activeWorkspace.logo_url)}
                       alt={activeWorkspace?.name || ""}
                       className="w-8 h-8 border border-border object-cover shrink-0" />
                ) : (
                  <div className="w-8 h-8 border border-border bg-muted text-foreground/70 flex items-center justify-center font-bold text-sm shrink-0">
                    {(activeWorkspace?.name || "?").slice(0, 1).toUpperCase()}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="label-eyebrow text-[10px]">Workspace</div>
                  <div className="font-semibold text-sm truncate" data-testid="workspace-switcher-name">{activeWorkspace?.name || "—"}</div>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        {!collapsed && (
          <div className="px-3 py-2 border-b border-border space-y-2" data-testid="nav-toolbox">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={navQuery}
                onChange={(e) => setNavQuery(e.target.value)}
                placeholder="Filter menu…"
                data-testid="nav-search-input"
                className="w-full text-xs pl-7 pr-2 py-1.5 bg-muted/40 focus:bg-background border border-transparent focus:border-primary/40 outline-none rounded transition"
              />
              {navQuery && (
                <button
                  type="button"
                  onClick={() => setNavQuery("")}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  data-testid="nav-search-clear"
                  aria-label="Clear filter"
                ><X className="w-3 h-3" /></button>
              )}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => { setAllExpandedTarget(true); setAllExpandedTick((t) => t + 1); }}
                className="flex-1 text-[10px] uppercase tracking-wide px-2 py-1 border border-border hover:border-primary/40 hover:bg-muted transition"
                data-testid="nav-expand-all"
              >Expand all</button>
              <button
                type="button"
                onClick={() => { setAllExpandedTarget(false); setAllExpandedTick((t) => t + 1); }}
                className="flex-1 text-[10px] uppercase tracking-wide px-2 py-1 border border-border hover:border-primary/40 hover:bg-muted transition"
                data-testid="nav-collapse-all"
              >Collapse all</button>
            </div>
          </div>
        )}

        <nav className="flex-1 py-3 px-2 space-y-1 overflow-y-auto min-h-0" data-testid="app-nav">
          {NAV_GROUPS.map((group) => (
            <NavGroup
              key={group.key}
              group={group}
              collapsed={collapsed}
              filter={navQuery}
              expandSignal={allExpandedTarget}
              expandTick={allExpandedTick}
              onClick={() => setDrawerOpen(false)}
            />
          ))}
        </nav>

        <div className="border-t border-border p-3">
          <DropdownMenu>
            <DropdownMenuTrigger className="w-full px-3 py-2 hover:bg-muted text-left flex items-center gap-3" data-testid="user-menu-btn">
              <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-xs overflow-hidden">
                {user?.avatar_data_url ? (
                  <img src={user.avatar_data_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  user?.name?.[0]?.toUpperCase()
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{user?.name}</div>
                <div className="text-[10px] text-muted-foreground truncate">{user?.email}</div>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              {user?.is_super_admin && (
                <DropdownMenuItem onClick={() => nav("/super-admin")} data-testid="user-menu-superadmin">
                  <Shield className="w-4 h-4 mr-2" /> Super Admin
                </DropdownMenuItem>
              )}
              {user?.is_super_admin && (
                <DropdownMenuItem onClick={() => nav("/app/settings/scheduler")} data-testid="user-menu-scheduler">
                  <Server className="w-4 h-4 mr-2" /> Scheduler ops
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="user-menu-profile">
                <UserIconLucide className="w-4 h-4 mr-2" /> My profile
              </DropdownMenuItem>
              {/* Iter Feb 2026 R33 · Gate the settings entry point */}
              {(user?.is_super_admin
                  || hasPerm("workspace.admin")
                  || hasPerm("workspace.manage")
                  || hasPerm("workspace.settings.view")) && (
                <DropdownMenuItem onClick={() => nav("/app/settings")} data-testid="user-menu-settings">
                  <SettingsIcon className="w-4 h-4 mr-2" /> Workspace settings
                </DropdownMenuItem>
              )}
              {installPrompt && (
                <DropdownMenuItem onClick={triggerInstall} data-testid="user-menu-install-app">
                  <Download className="w-4 h-4 mr-2" /> Install app
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="user-menu-logout">
                <LogOut className="w-4 h-4 mr-2" /> Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      <main className="min-w-0">
        {/* Top utility bar */}
        {/* R61 · Header must sit ABOVE the sidebar (z-50) so its child
             popovers (Tasks/Timesheet, Jobs, Alerts, Notifications) don't
             get clipped by the left nav. Was `z-30` which trapped every
             popover in the header's stacking context — bumped to z-[60]. */}
        {/* R68 · Mobile-responsive header. Below sm: only the menu
            button, compact search, reminders and notifications show —
            the secondary widgets (profile / plan / timesheet / jobs /
            wa) tuck away on sm+ to keep the mobile view usable.
            R159mm · Feb 2026 — Uniform mobile chrome: every direct
            child button/widget in the header now conforms to `h-8`
            with square touch targets so nothing sticks out taller
            than its neighbours on phones.
            R159nn · Feb 2026 — Mobile polish: replaced the hamburger
            icon with the workspace's square brand icon (falls back
            to the initial chip), dropped borders on mobile buttons
            since the icons are already legible, and switched the
            top-level spacing to `justify-between` so the search
            block, timesheet chip and bell cluster are evenly
            distributed. */}
        <div className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-[60] px-2 sm:px-4 md:px-6 h-12 flex items-center justify-between gap-1 sm:gap-2 [&>button]:h-8 sm:[&>button]:h-9">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="md:hidden text-foreground/60 hover:text-foreground w-8 h-8 flex items-center justify-center shrink-0"
            data-testid="sidebar-open-btn"
            aria-label="Open menu"
          >
          <MobileHeaderBrandIcon branding={branding} />
          </button>
          {navOverrides?.["header-search"] === "hide" ? <div className="flex-1" /> : (
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-1.5 text-[11px] sm:text-xs text-muted-foreground hover:text-foreground sm:border sm:border-border px-2 sm:px-3 h-8 transition flex-1 sm:flex-none min-w-0"
            data-testid="open-search-btn"
          >
            <Search className="w-3.5 h-3.5 shrink-0" /> <span className="hidden sm:inline">Search</span>
            <kbd className="hidden md:inline-block text-[9px] font-mono border border-border px-1">⌘K</kbd>
          </button>
          )}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink min-w-0">
            {/* R126 · Feb 2026 — Below sm, the secondary widgets (Create,
                Daily plan, WhatsApp, Jobs) tuck behind a single "⋯"
                mobile overflow menu so the phone header doesn't
                horizontally overflow.
                R138j · Feb 2026 — TimesheetWidget stays visible on
                mobile too (shows today's h:m clock-in) — tapping it
                opens the same popup as desktop.
                R138e · Each header widget can be hidden per role via
                `header-<slug>` in `nav_overrides` (Role editor).
                R159pp · Feb 2026 — HeaderProfile moved to the far
                right (after bells) so the user chip is the rightmost
                item in the header on every viewport. */}
            <div className="hidden sm:flex items-center gap-1 sm:gap-2">
              {navOverrides?.["header-create"] !== "hide" && <HeaderCreateButton />}
              {/* Oct 2026 · User request: move the Timesheet pill to
                  the LEFT of the Daily plan button so logging hours is
                  the first action in the group. */}
              {navOverrides?.["header-timesheet"] !== "hide" && <TimesheetWidget />}
              {navOverrides?.["header-daily-plan"] !== "hide" && (
                <div className="hidden md:flex"><DailyPlanHeaderButton /></div>
              )}
              {navOverrides?.["header-jobs"] !== "hide" && (
                <div className="hidden md:flex"><JobsIndicator /></div>
              )}
              {navOverrides?.["header-whatsapp"] !== "hide" && <WhatsAppHeaderButton />}
            </div>
            <div className="sm:hidden"><HeaderMobileOverflow /></div>
          </div>
          {/* R159mm · Feb 2026 — Wrap the bell / notification widgets
              in a consistent flex container that forces a uniform
              `h-8` mobile target so nothing sticks out above its
              neighbours on phone widths. */}
          <div className="flex items-center gap-0.5 sm:gap-1 [&>*]:h-8 [&>*]:flex [&>*]:items-center">
            <MyJourneyHeaderButton />
            {navOverrides?.["header-reminders"] !== "hide" && <RemindersBell />}
            {navOverrides?.["header-watch-list"] !== "hide" && <WatchListHeaderButton />}
            {navOverrides?.["header-notes"] !== "hide" && <NotesHeaderButton />}
            {navOverrides?.["header-notifications"] !== "hide" && <NotificationsBell />}
            {navOverrides?.["header-profile"] !== "hide" && <HeaderProfile />}
          </div>
        </div>
        <DailyPlanReminderBanner navOverrides={navOverrides} />
        <FestivalBanner />
        <CelebrationOverlay />
        <ErrorBoundary><Outlet /></ErrorBoundary>
      </main>

      <CommandPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <CelebrationLayer />
      <HuddleJoinWidget />
      <ReminderPopup />
      <FloatingWaWidget />
      <WhatsAppLiveAlerts />
      <PageTelemetry />
      <ScreenshotReporter />
      <GlobalFeedbackLauncher />
    </div>
    </EmployeeProfileProvider>
    </SopRunnerProvider>
  );
}

function NavGroup({ group, collapsed, onClick, filter = "", expandSignal, expandTick }) {
  const { can, ready, navOverrides, isAdminRole, modules } = useWorkspacePerms();
  const { user } = useAuth();
  // R268ii · Feb 2026 — Cross-device sidebar state. Server prefs override
  // the legacy per-device localStorage on first load; ephemeral local
  // state ensures instant toggle feel while the debounced PATCH flies.
  const { getPref, setPref, ready: prefsReady } = useUserPreferences();
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem(`co_navgrp_${group.key}`) !== "0"; } catch { return true; }
  });
  // Hydrate from server prefs once they arrive.
  useEffect(() => {
    if (!prefsReady) return;
    const v = getPref(`sidebar.${group.key}.collapsed`);
    if (v === true) setOpen(false);
    else if (v === false) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsReady, group.key]);
  useEffect(() => {
    try { localStorage.setItem(`co_navgrp_${group.key}`, open ? "1" : "0"); } catch { /* ignore */ }
    // Mirror to server prefs (debounced by the hook).
    if (prefsReady) setPref({ sidebar: { [group.key]: { collapsed: !open } } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, group.key, prefsReady]);
  // Expand/Collapse All external signal
  useEffect(() => {
    if (expandSignal === true) setOpen(true);
    else if (expandSignal === false) setOpen(false);
  }, [expandTick, expandSignal]);

  const complianceBadge = useComplianceBadge();
  const badgeCountFor = (testid) => {
    if (!complianceBadge) return 0;
    if (testid === "nav-attendance") return complianceBadge.unacknowledged_violations || 0;
    if (testid === "nav-now") return complianceBadge.pending_anomalies || 0;
    return 0;
  };

  const q = (filter || "").trim().toLowerCase();
  // Iter 253j · SECURITY hardening. Nav items must be explicitly permitted.
  // Previously items without a `perm` field were shown to everyone, so a
  // "Sales & Marketing" limited role saw every uncategorised menu (timesheet,
  // attendance, integrations, etc). Now missing `perm` is treated as
  // admin-only — limited/custom roles only see items whose `perm` codes are
  // in their permission set.
  const items = group.items.filter((n) => {
    // R268hh · Feb 2026 — Sub-section dividers are pure UI; never filtered.
    if (n.divider) return true;
    // R76 · super-admin-only nav items (e.g. Scheduler ops)
    if (n.superAdminOnly && !user?.is_super_admin) return false;
    // Feb 2026 · Module kill-switch (workspace toggle + per-role perm).
    // A nav entry tagged with `module: "brands"` is hidden entirely when
    // the effective module flag is off, regardless of individual perms.
    if (n.module && modules && modules[n.module] === false) return false;
    const ov = navOverrides?.[n.testid];
    if (ov === "hide") return false;
    if (ov === "show") { /* explicit show always wins */ }
    else if (n.perm && n.perm.length) {
      if (!(ready && n.perm.some((p) => can(p)))) return false;
    } else {
      // Uncategorised menu → admin-tier only.
      if (!(ready && isAdminRole)) return false;
    }
    if (q && !(n.label || "").toLowerCase().includes(q) && !(group.label || "").toLowerCase().includes(q)) return false;
    return true;
  });
  // Drop leading / trailing / duplicate dividers so groups look clean
  // even when perms hide every item between two dividers.
  const cleanItems = [];
  for (const it of items) {
    if (it.divider) {
      if (cleanItems.length === 0) continue;
      if (cleanItems[cleanItems.length - 1].divider) cleanItems.pop();
      cleanItems.push(it);
    } else {
      cleanItems.push(it);
    }
  }
  while (cleanItems.length && cleanItems[cleanItems.length - 1].divider) cleanItems.pop();
  const finalItems = cleanItems;
  if (finalItems.filter((n) => !n.divider).length === 0) return null;
  // While filtering, auto-expand any group with matches
  const shownOpen = q ? true : open;

  if (collapsed) {
    return (
      <div className="space-y-1">
        {finalItems.filter((n) => !n.divider).map((n) => {
          const badge = badgeCountFor(n.testid);
          return (
            <NavLink
              key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={onClick} title={n.label}
              className={({ isActive }) => `relative flex items-center justify-center px-3 py-2.5 text-sm rounded-sm transition ${isActive ? "bg-foreground text-background font-medium" : "hover:bg-muted text-foreground/80"}`}
            >
              <n.icon className="w-4 h-4 shrink-0" />
              {badge > 0 && (
                <span className="absolute top-0 right-0 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`${n.testid}-badge`}>{badge > 99 ? "99+" : badge}</span>
              )}
            </NavLink>
          );
        })}
      </div>
    );
  }
  return (
    <div className="mb-1.5" data-testid={`nav-group-${group.key}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1 px-3 py-1 text-[10px] uppercase tracking-wide font-bold text-muted-foreground hover:text-foreground"
        data-testid={`nav-group-toggle-${group.key}`}
      >
        {shownOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        <span className="truncate">{group.label}</span>
      </button>
      {shownOpen && (
        <div className="space-y-px mt-0.5">
          {finalItems.map((n, idx) => {
            // R268hh · Feb 2026 — Sub-section divider inside a group.
            // Items with `divider: true` render as a small header row.
            if (n.divider) {
              return (
                <div key={`div-${idx}`}
                       className="px-3 pt-2 pb-0.5 text-[9px] uppercase tracking-widest font-semibold text-muted-foreground/70"
                       data-testid={`nav-divider-${(n.label || "").toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </div>
              );
            }
            const badge = badgeCountFor(n.testid);
            return (
              <NavLink
                key={n.to} to={n.to} end={n.end} data-testid={n.testid} onClick={onClick}
                className={({ isActive }) => `flex items-center gap-2.5 px-3 py-1.5 text-xs rounded-sm transition ${isActive ? "bg-foreground text-background font-medium" : "hover:bg-muted text-foreground/80"}`}
              >
                <n.icon className="w-3.5 h-3.5 shrink-0" />
                <span className="flex-1 truncate">{n.label}</span>
                {badge > 0 && (
                  <span className="min-w-5 h-4 px-1.5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center" data-testid={`${n.testid}-badge`}>{badge > 99 ? "99+" : badge}</span>
                )}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ImpersonationBanner() {
  const { impersonation, activeWorkspace, refresh } = useAuth();
  // Iter 253n · Server-authoritative — derive the banner from /auth/me's
  // `impersonation` claim. Fall back to the sessionStorage flag only for
  // the split-second between initiating impersonation (before /auth/me is
  // refetched) and the first refresh completing.
  const [localTarget, setLocalTarget] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem("co_impersonation_target") || "null"); } catch { return null; }
  });
  const active = impersonation || localTarget;
  useEffect(() => {
    if (active) document.body.classList.add("acq-impersonating");
    else document.body.classList.remove("acq-impersonating");
    return () => document.body.classList.remove("acq-impersonating");
  }, [active]);
  if (!active) return null;
  const targetName = impersonation?.target_name || impersonation?.target_email
    || localTarget?.name || localTarget?.email || "user";
  const impersonatorName = impersonation?.impersonator_name || "Admin";
  const wsName = activeWorkspace?.name || "";
  const exit = async () => {
    // R148 · Feb 2026 — Server-authoritative exit. Fall back to the
    // sessionStorage restore only if the backend endpoint isn't reachable.
    try {
      const { data } = await api.post("/auth/exit-impersonation");
      if (data?.token) {
        localStorage.setItem("co_token", data.token);
        sessionStorage.removeItem("co_impersonation_target");
        sessionStorage.removeItem("co_impersonation_origin");
        setLocalTarget(null);
        window.location.href = "/app";
        return;
      }
    } catch (e) {
      // Fall through to legacy sessionStorage path
      console.warn("exit-impersonation endpoint failed, falling back", e);
    }
    const original = sessionStorage.getItem("co_impersonation_origin");
    if (original) localStorage.setItem("co_token", original);
    sessionStorage.removeItem("co_impersonation_target");
    sessionStorage.removeItem("co_impersonation_origin");
    setLocalTarget(null);
    // Force a hard reload so all in-memory context (perms cache, workspace
    // selection, /auth/me) is rebuilt from the original admin token.
    if (refresh) { try { refresh(); } catch { /* ignore */ } }
    window.location.href = "/app";
  };
  return (
    <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-400 text-black px-4 flex items-center gap-3 text-sm font-semibold shadow-md border-b-2 border-amber-600" style={{ height: 40 }} data-testid="impersonation-banner">
      <span className="inline-flex items-center gap-1.5 text-lg" aria-hidden="true">⚠️</span>
      <span className="hidden sm:inline" data-testid="impersonation-banner-text">
        You&apos;re impersonating <strong>{targetName}</strong>
        {wsName && <> in workspace <strong>{wsName}</strong></>}
        {impersonation && <span className="text-xs font-normal opacity-80 ml-2">· signed in as {impersonatorName}</span>}
      </span>
      <span className="sm:hidden" data-testid="impersonation-banner-text-mobile">
        Impersonating <strong>{targetName}</strong>
      </span>
      <span className="text-[10px] font-normal opacity-80 hidden lg:inline ml-2">All actions are logged.</span>
      <button onClick={exit} className="ml-auto bg-black text-amber-400 px-3 py-1 text-xs font-bold hover:bg-neutral-800 transition-colors rounded-none" data-testid="impersonation-exit">
        Exit impersonation
      </button>
    </div>
  );
}


/**
 * Sep 2026 · r-agent-feed — sidebar "N agents · live" chip fed by real
 * workspace data (via useAgentsLive). Hover to see per-agent activity
 * in a Popover panel. Two visual states:
 *   · live    → green pulsing dot + "N agents · live"
 *   · standby → orange dot + "agents on standby" (shown when all
 *                agents are idle OR randomly ~15% of the time so it
 *                feels natural even on busy workspaces)
 */
function SidebarAgentsCount({ collapsed }) {
  const feed = useAgentsLive();
  const { user, activeWorkspace } = useAuth();
  // r-agent-navigate-gate · only Owner / Admin can click through to
  // an agent's home. Everyone else sees the list read-only.
  const role = activeWorkspace?.role || activeWorkspace?.membership_role;
  const canNavigate = user?.is_super_admin || role === "owner" || role === "admin";
  const [randomStandby, setRandomStandby] = useState(false);
  useEffect(() => {
    // 15%-ish chance of flipping into standby for 8-14s every 45-90s.
    let alive = true;
    let t;
    const tick = () => {
      if (!alive) return;
      if (!randomStandby && Math.random() < 0.15) {
        setRandomStandby(true);
        t = setTimeout(() => {
          if (!alive) return;
          setRandomStandby(false);
          t = setTimeout(tick, 45000 + Math.floor(Math.random() * 45000));
        }, 8000 + Math.floor(Math.random() * 6000));
      } else {
        t = setTimeout(tick, 45000 + Math.floor(Math.random() * 45000));
      }
    };
    t = setTimeout(tick, 20000 + Math.floor(Math.random() * 20000));
    return () => { alive = false; clearTimeout(t); };
  }, [randomStandby]);

  if (collapsed) return null;
  const liveCount = feed?.live_count ?? 0;
  const isStandby = randomStandby || (feed && liveCount === 0);
  const agents = feed?.agents || [];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="mt-1.5 flex items-center gap-1.5 text-[10px] text-muted-foreground pl-1 select-none hover:text-foreground/80 transition-colors"
          data-testid="sidebar-agents-count"
          data-mode={isStandby ? "standby" : "live"}
          aria-label="AI agents live status"
        >
          <span className="relative flex items-center justify-center" aria-hidden="true">
            <span className={`w-1 h-1 rounded-full ${isStandby ? "bg-orange-500" : "bg-emerald-500"}`} />
            <span className={`absolute w-2 h-2 rounded-full ${isStandby ? "bg-orange-500/30" : "bg-emerald-500/30"} animate-ping`} />
          </span>
          <span>
            {isStandby ? (
              <span key="standby" className="login-fade-in">agents on standby</span>
            ) : (
              <span key={`live-${liveCount}`} className="login-fade-in">
                <span className="font-semibold text-foreground/70">{liveCount}</span>
                {" agent"}{liveCount === 1 ? "" : "s"}{" · live"}
              </span>
            )}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        className="w-72 p-0 border-border"
        data-testid="sidebar-agents-panel"
      >
        <div className="px-3 py-2 border-b border-border flex items-center gap-2">
          <span className="relative flex items-center justify-center" aria-hidden="true">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="absolute w-3 h-3 rounded-full bg-emerald-500/30 animate-ping" />
          </span>
          <div className="text-xs font-semibold">AI agents · workspace</div>
          <div className="ml-auto text-[10px] text-muted-foreground font-mono">
            {liveCount}/{feed?.total ?? 0} live
          </div>
        </div>
        <div className="max-h-80 overflow-y-auto divide-y divide-border">
          {agents.length === 0 ? (
            <div className="px-3 py-3 text-[11px] text-muted-foreground">Warming up…</div>
          ) : (
            agents.map((a) => {
              const Row = canNavigate && a.link ? Link : "div";
              const rowProps = canNavigate && a.link
                ? { to: a.link }
                : { role: "listitem" };
              return (
                <Row
                  key={a.key}
                  {...rowProps}
                  className={`px-3 py-2 flex items-start gap-2 text-[11px] transition-colors ${
                    canNavigate && a.link ? "hover:bg-muted/60 cursor-pointer" : "cursor-default"
                  }`}
                  data-testid={`agent-row-${a.key}`}
                >
                  <span
                    className={`mt-1 w-1.5 h-1.5 rounded-full shrink-0 ${a.state === "live" ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold truncate">{a.label}</span>
                      <AgentSparkline data={a.spark} live={a.state === "live"} />
                    </div>
                    <div className="text-muted-foreground truncate">{a.activity}</div>
                  </div>
                  {(a.count || 0) > 0 && (
                    <span className="font-mono font-semibold text-foreground/70 tabular-nums self-center">{a.count}</span>
                  )}
                </Row>
              );
            })
          )}
        </div>
        {!canNavigate && (
          <div className="px-3 py-2 border-t border-border text-[10px] text-muted-foreground bg-muted/30" data-testid="sidebar-agents-panel-note">
            Owners and admins can click through to each agent's workspace.
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}


/**
 * Sep 2026 · r-agent-sparkline — tiny 7-day trend chart shown next to
 * each agent name in the sidebar hover panel. Inline SVG (no chart
 * library) — 48×14 px, mixed bar/line, primary tint for live agents,
 * muted for idle. Hover reveals a tooltip with the exact day counts.
 */
function AgentSparkline({ data, live }) {
  const arr = Array.isArray(data) && data.length ? data : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...arr);
  const w = 48;
  const h = 14;
  const step = w / (arr.length - 1 || 1);
  const points = arr.map((v, i) => {
    const x = i * step;
    const y = h - (v / max) * h;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  const strokeColor = live ? "hsl(var(--primary))" : "hsl(var(--muted-foreground) / 0.4)";
  const fillColor = live ? "hsl(var(--primary) / 0.18)" : "hsl(var(--muted-foreground) / 0.08)";
  const total = arr.reduce((s, v) => s + v, 0);
  return (
    <span className="inline-flex items-center gap-1 shrink-0" title={`Last 7 days · total ${total} (${arr.join(", ")})`}>
      <svg viewBox={`0 0 ${w} ${h}`} width={w} height={h} className="overflow-visible">
        <polygon
          points={`0,${h} ${points} ${w},${h}`}
          fill={fillColor}
          stroke="none"
        />
        <polyline
          points={points}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Latest-day dot for emphasis */}
        <circle
          cx={(arr.length - 1) * step}
          cy={h - (arr[arr.length - 1] / max) * h}
          r="1.4"
          fill={strokeColor}
        />
      </svg>
    </span>
  );
}

